import kiccEasyCardQueries from '@apis/queries/kiccEasyCardQueries';
import Button from '@components/Button';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import DeleteCircleFill from '@icons/DeleteCircleFill';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useUpdateEasyCardName from './hooks/useUpdateEasyCardName';

declare module '@stackflow/config' {
    interface Register {
        EasyCardNameBottomSheet: {
            cardId: string;
        }
    }
}

const CARD_NAME_MAX_LENGTH = 15;

const EasyCardNameBottomSheet: ActivityComponentType<'EasyCardNameBottomSheet'> = ({ params }) => {
    const { cardId } = params;
    const { t } = useTranslation('easyCard');
    const { pop } = useFlow();
    const { data } = useQuery(kiccEasyCardQueries.list());
    const currentCardName = data?.cards.find((card) => card.cardId === cardId)?.cardName;
    const [editedCardName, setCardName] = useState<string>();
    const cardName = editedCardName ?? currentCardName ?? '';
    const { handleUpdateCardName } = useUpdateEasyCardName(cardId);
    return (
        <BottomSheet>
            <Header>
                <Text size='title' fontWeight='bold'>
                    {t('rename.title')}
                </Text>
            </Header>
            <Contents spacing={1} fullWidth>
                <TextField value={cardName}
                    setValue={setCardName}
                    maxLength={CARD_NAME_MAX_LENGTH}
                    placeholder={t('rename.placeholder')}
                    trailing={cardName && (
                        <ClearButton type='button'
                            aria-label={t('rename.clear')}
                            onClick={() => setCardName('')}>
                            <DeleteCircleFill/>
                        </ClearButton>
                    )} />
                <Text color={colors.textThird} size='body-long'>
                    {t('rename.helper', { max: CARD_NAME_MAX_LENGTH })}
                </Text>
            </Contents>
            <ButtonGroup spacing={2} fullWidth>
                <Button fullWidth
                    noPadding
                    variant='tone'
                    onClick={() => pop()}>
                    {t('rename.cancel')}
                </Button>
                <Button fullWidth
                    noPadding
                    disabled={!cardName.trim()}
                    onClick={() => handleUpdateCardName(cardName)}>
                    {t('rename.confirm')}
                </Button>
            </ButtonGroup>
        </BottomSheet>
    );
};

const Header = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-5) 0;
`;

const Contents = styled(Column)`
    padding: var(--spacing-4);
`;

const ClearButton = styled.button`
    display: flex;
    align-items: center;
    margin-right: 16px;
    padding: 2px;
    background-color: transparent;
`;

const ButtonGroup = styled(Row)`
    padding: var(--spacing-4) var(--spacing-4) calc(var(--spacing-4) + var(--safe-area-bottom));
`;

export default EasyCardNameBottomSheet;
